import React, { useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { platformApiFetch } from './platformApi';
import { usePlatformAuth } from '../context/PlatformAuthContext';

const JOB_TYPES = [
  { value: 'platform_tenants_csv', label: 'Tenants directory (CSV)' },
  { value: 'platform_users_csv', label: 'Platform users (CSV)' },
  { value: 'platform_audit_digest_csv', label: 'Audit digest (CSV)' },
  { value: 'custom', label: 'Custom (filters only)' }
];

export default function PlatformExportJobFormPage() {
  const navigate = useNavigate();
  const { user, ready } = usePlatformAuth();
  const [jobType, setJobType] = useState('platform_tenants_csv');
  const [filtersText, setFiltersText] = useState('{}');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    let filters_json;
    try {
      filters_json = JSON.parse(filtersText.trim() || '{}');
    } catch (_err) {
      setError('Filters must be valid JSON');
      setSaving(false);
      return;
    }
    try {
      const res = await platformApiFetch('/api/platform/export-jobs', {
        method: 'POST',
        body: { job_type: jobType, filters_json }
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Create failed');
      navigate(`/platform/export-jobs/${json.job.id}`, { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (!ready) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (user?.role !== 'super_admin') {
    return <Navigate to="/platform/analytics" replace />;
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        New platform export
      </Typography>
      <Button component={Link} to="/platform/export-jobs" sx={{ mb: 2 }}>
        Back
      </Button>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Paper sx={{ p: 2, maxWidth: 720 }}>
        <Box component="form" onSubmit={submit}>
          <FormControl fullWidth margin="normal">
            <InputLabel>Job type</InputLabel>
            <Select label="Job type" value={jobType} onChange={(e) => setJobType(e.target.value)}>
              {JOB_TYPES.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Filters (JSON)"
            value={filtersText}
            onChange={(e) => setFiltersText(e.target.value)}
            margin="normal"
            multiline
            minRows={6}
            sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
          />
          <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
            <Button type="submit" variant="contained" disabled={saving}>
              {saving ? 'Creating…' : 'Create job'}
            </Button>
            <Button component={Link} to="/platform/export-jobs">
              Cancel
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}
